import { useEffect, useRef, useState } from 'react'

import { antwortBeobachten, netzBeobachten, netzMarke } from '../api/client'

/**
 * An address for a picture that asks again once the network is back
 * (1.50.0, from use: "wenn die App ohne Netz geöffnet wird, funktioniert
 * das. Aber nachdem man online ist, werden zwar die Beiträge gezeigt im
 * Chat, aber nicht das Hintergrundbild, und die Bildersymbole der Reisen").
 *
 * The cause is the browser, not the server: an `<img>` (or a CSS
 * background) that failed while offline keeps its address, and an address
 * that does not change is never requested again. New posts appear because
 * they are new elements; the cover and the background were already there.
 * So the address gets a counter (`?v=n`) once a load failed and the net
 * returned -- a new address is a new request, and `key={adresse}` makes it
 * a new element. Nothing is added while nothing failed: the pictures are
 * served `immutable`, and a needless counter would cost every reload.
 *
 * **Three signs of the net returning, and none of them is
 * `navigator.onLine`** (1.50.1). The first cut asked the browser's flag
 * before retrying and listened to `online` and to the app's own mark; on
 * an iPad in the home-screen app the trip list kept Safari's broken-picture
 * sign after the net was back, while the posts arrived. So: the browser's
 * `online`, the app's mark flipping (`netzBeobachten`), and **every answer
 * from the server** (`antwortBeobachten`) -- the posts arriving is the one
 * sign that was demonstrably there. Whichever comes first retries once;
 * the next sign retries again only if the picture is still missing.
 *
 * **And "missing" is asked, not only remembered.** `fehlgeschlagen` is the
 * `error` event; `fehlt` looks at the picture itself (`complete` without a
 * width), for the case that no `error` ever came. Bounded: five retries per
 * address, so that a picture the server really does not have does not ask
 * once per answer for ever.
 */
const HOECHSTENS = 5

export function useNetzbild(
  src: string | null,
  fehlt?: () => boolean,
): { adresse: string | null; versuch: number; fehlgeschlagen: () => void } {
  const [versuch, setVersuch] = useState(0)
  const kaputt = useRef(false)

  useEffect(() => {
    setVersuch(0)
    kaputt.current = false
  }, [src])

  useEffect(() => {
    if (src === null || versuch >= HOECHSTENS) return
    let erledigt = false
    const nochmal = () => {
      if (erledigt) return
      if (!kaputt.current && !(fehlt?.() ?? false)) return
      erledigt = true
      kaputt.current = false
      setVersuch((n) => n + 1)
    }
    window.addEventListener('online', nochmal)
    // The mark flips both ways; only the way back is a sign. The first cut
    // retried on the flip to "gone" as well and burnt one attempt offline.
    const abNetz = netzBeobachten(() => {
      if (netzMarke()) nochmal()
    })
    const abAntwort = antwortBeobachten(nochmal)
    return () => {
      window.removeEventListener('online', nochmal)
      abNetz()
      abAntwort()
    }
    // `fehlt` reads the current element; the closure needs no re-subscription.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, versuch])

  const adresse =
    src === null ? null : versuch === 0 ? src : `${src}${src.includes('?') ? '&' : '?'}v=${versuch}`
  return {
    adresse,
    versuch,
    fehlgeschlagen: () => {
      kaputt.current = true
    },
  }
}
